import { useEffect, useRef } from 'react';

import { useAppSelector } from '@src/core/hooks';
import { resolvePaymentFailureReason, type PaymentMethodCode } from '@src/domains/payments';
import {
  chargeAttemptCountSelector,
  externalAuthChallengeCountSelector,
  paymentErrorsSelector,
} from '@src/features/payments/store/payment';

import { useCheckoutTracking } from './useCheckoutTracking';

type Params = {
  pixQrVisible: boolean;
  selectedMethod?: PaymentMethodCode;
};

export const useCheckoutPaymentEvents = ({ pixQrVisible, selectedMethod }: Params) => {
  const tracking = useCheckoutTracking();

  const paymentErrors = useAppSelector(paymentErrorsSelector);
  const challengeCount = useAppSelector(externalAuthChallengeCountSelector);
  const attemptCount = useAppSelector(chargeAttemptCountSelector);

  // Both counts come from the store: leaving the payment step and coming back would restart a
  // component ref at one, reporting a retry as the first attempt.
  const challengeCountRef = useRef(challengeCount);
  const hadErrorsRef = useRef(Object.keys(paymentErrors ?? {}).length > 0);
  const pixQrVisibleRef = useRef(false);

  useEffect(() => {
    if (challengeCount > challengeCountRef.current) {
      tracking.threeDsShown(attemptCount);
    }

    challengeCountRef.current = challengeCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the attempt count is read, never watched
  }, [challengeCount, tracking]);

  useEffect(() => {
    const reason = resolvePaymentFailureReason(paymentErrors);

    // The method on screen when the charge was refused is the one it was attempted with.
    if (reason && !hadErrorsRef.current) {
      tracking.paymentFailed({ reason, attemptNumber: attemptCount, method: selectedMethod });
    }

    hadErrorsRef.current = reason != null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the attempt count and the method are read, never watched
  }, [paymentErrors, tracking]);

  useEffect(() => {
    if (pixQrVisible && !pixQrVisibleRef.current) {
      tracking.pixQrShown();
    }

    pixQrVisibleRef.current = pixQrVisible;
  }, [pixQrVisible, tracking]);
};

export default useCheckoutPaymentEvents;
